import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";

const FrequentlyAskedQuestions = () => {
  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <p className="uppercase text-sm font-medium tracking-wider text-[#9b87f5] mb-2">FREQUENTLY ASKED QUESTIONS</p>
          <h2 className="text-3xl font-bold mb-4">Common Questions About RoundOne AI</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Everything you need to know about our AI-powered screening platform
          </p>
        </div>
        
        <div className="max-w-3xl mx-auto space-y-4">
          {/* FAQ Item 1 */}
          <div className="bg-[#F8F7FF] rounded-xl border border-[#E5DEFF] overflow-hidden transition-all duration-300 hover:border-[#9b87f5] group">
            <details className="group">
              <summary className="flex items-center justify-between gap-3 p-6 cursor-pointer">
                <h3 className="text-lg font-semibold">How does the AI interviewer work?</h3>
                <span className="relative flex-shrink-0 ml-1.5 w-5 h-5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="absolute transition-opacity opacity-100 group-open:opacity-0" fill="none" viewBox="0 0 24 24" stroke="#9b87f5" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" className="absolute transition-opacity opacity-0 group-open:opacity-100" fill="none" viewBox="0 0 24 24" stroke="#9b87f5" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
                  </svg>
                </span>
              </summary>
              <div className="px-6 pb-6 text-muted-foreground">
                <p>Our AI interviewer uses advanced natural language processing to conduct realistic interviews. Candidates can talk to our AI directly in their browser or request a call back at their convenience. They answer role-specific questions while the AI adapts based on their responses, asking follow-up questions to thoroughly assess each skill area. After the interview, you receive detailed analysis including skill scores, candidate summaries, and recommended next steps.</p>
              </div>
            </details>
          </div>
          
          {/* FAQ Item 2 */}
          <div className="bg-[#F8F7FF] rounded-xl border border-[#E5DEFF] overflow-hidden transition-all duration-300 hover:border-[#9b87f5] group">
            <details className="group">
              <summary className="flex items-center justify-between gap-3 p-6 cursor-pointer">
                <h3 className="text-lg font-semibold">What types of roles can RoundOne screen for?</h3>
                <span className="relative flex-shrink-0 ml-1.5 w-5 h-5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="absolute transition-opacity opacity-100 group-open:opacity-0" fill="none" viewBox="0 0 24 24" stroke="#9b87f5" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" className="absolute transition-opacity opacity-0 group-open:opacity-100" fill="none" viewBox="0 0 24 24" stroke="#9b87f5" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
                  </svg>
                </span>
              </summary>
              <div className="px-6 pb-6 text-muted-foreground">
                <p>RoundOne AI can screen for virtually any role across industries. Whether you're hiring C-suite executives or warehouse staff, healthcare professionals or creative designers, our platform adapts to evaluate the specific skills needed for success. From highly specialized roles requiring technical expertise to customer-facing positions demanding exceptional soft skills, our customizable assessments provide consistent, data-driven evaluations for any position in your organization.</p>
              </div>
            </details>
          </div>
          
          {/* FAQ Item 3 */}
          <div className="bg-[#F8F7FF] rounded-xl border border-[#E5DEFF] overflow-hidden transition-all duration-300 hover:border-[#9b87f5] group">
            <details className="group">
              <summary className="flex items-center justify-between gap-3 p-6 cursor-pointer">
                <h3 className="text-lg font-semibold">How long does implementation take?</h3>
                <span className="relative flex-shrink-0 ml-1.5 w-5 h-5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="absolute transition-opacity opacity-100 group-open:opacity-0" fill="none" viewBox="0 0 24 24" stroke="#9b87f5" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" className="absolute transition-opacity opacity-0 group-open:opacity-100" fill="none" viewBox="0 0 24 24" stroke="#9b87f5" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
                  </svg>
                </span>
              </summary>
              <div className="px-6 pb-6 text-muted-foreground">
                <p>You can be up and running with RoundOne in minutes. Creating your first assessment takes as little as 5 minutes, and you can send candidate invitations immediately after. For ATS integration, our two-click setup process typically takes less than 15 minutes. We also offer white-glove implementation support for Enterprise plans to assist with custom workflows and configurations.</p>
              </div>
            </details>
          </div>
          
          {/* FAQ Item 4 */}
          <div className="bg-[#F8F7FF] rounded-xl border border-[#E5DEFF] overflow-hidden transition-all duration-300 hover:border-[#9b87f5] group">
            <details className="group">
              <summary className="flex items-center justify-between gap-3 p-6 cursor-pointer">
                <h3 className="text-lg font-semibold">How do candidates feel about AI interviews?</h3>
                <span className="relative flex-shrink-0 ml-1.5 w-5 h-5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="absolute transition-opacity opacity-100 group-open:opacity-0" fill="none" viewBox="0 0 24 24" stroke="#9b87f5" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" className="absolute transition-opacity opacity-0 group-open:opacity-100" fill="none" viewBox="0 0 24 24" stroke="#9b87f5" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
                  </svg>
                </span>
              </summary>
              <div className="px-6 pb-6 text-muted-foreground">
                <p>Our candidate satisfaction rate is over 92%. Candidates appreciate the flexibility to complete interviews on their own schedule, the consistent and fair evaluation process, and the natural conversation flow with our AI. We've designed the experience to be engaging and professional, and many candidates report preferring it to traditional screening calls that often feel rushed or impersonal.</p>
              </div>
            </details>
          </div>
          
          {/* FAQ Item 5 */}
          <div className="bg-[#F8F7FF] rounded-xl border border-[#E5DEFF] overflow-hidden transition-all duration-300 hover:border-[#9b87f5] group">
            <details className="group">
              <summary className="flex items-center justify-between gap-3 p-6 cursor-pointer">
                <h3 className="text-lg font-semibold">How accurate are the AI assessments?</h3>
                <span className="relative flex-shrink-0 ml-1.5 w-5 h-5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="absolute transition-opacity opacity-100 group-open:opacity-0" fill="none" viewBox="0 0 24 24" stroke="#9b87f5" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" className="absolute transition-opacity opacity-0 group-open:opacity-100" fill="none" viewBox="0 0 24 24" stroke="#9b87f5" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
                  </svg>
                </span>
              </summary>
              <div className="px-6 pb-6 text-muted-foreground">
                <p>Our assessments achieve 88% accuracy compared to expert human evaluations. We're constantly training and refining our AI with input from industry experts across various domains. Our AI evaluates both the technical accuracy of responses and important nuances like problem-solving approach, communication clarity, and depth of knowledge. Each assessment includes confidence scores so you can easily identify areas that may benefit from additional evaluation.</p>
              </div>
            </details>
          </div>
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">Still have questions? We're here to help.</p>
          <Link to="/contact">
            <Button 
              className="bg-white text-roundone-dark-purple py-3 px-6 rounded-full border border-roundone-light-purple transition-all duration-300 hover:bg-[#9b87f5]/10 hover:border-[#9b87f5] hover:text-[#9b87f5] hover:shadow-sm"
            >
              Contact Support
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FrequentlyAskedQuestions; 